import {Injector} from '@angular/core';
import {Decimal} from 'decimal.js';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {DexStreamingInitResolverService} from '../../components/dex/dex-streaming-init-resolver.service';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {numToValueWithDel, vToDec} from '../../components/dex/dex-streaming/utils';
import {DexTrancheUtilityService} from '../../components/dex/dex-tranche-utility/dex-tranche-utility.service';
import {DynamicZyxDaemonService} from '../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {
    WidgetImplementation,
    WidgetImplementationEvent,
    WidgetImplementationEventFlags
} from '../../components/layout/widget/widget-implementation';
import {AppConfigService} from '../../services/config.service';
import {
    getSettingsWithUpdateField,
    getWidgetSettingsWithUpdatesStream,
    isSettingsUpdateEmptyOrSame,
    pipeSettingsCorrections,
    setWidgetSettings,
    updateWidgetSettings
} from '../components/common-widget.implementation';
import {DexLpTradeWidgetSettings} from '../dex-lp-trade-widget/settings';
import {DexTransferWidgetSettings} from './settings/dex-transfer-widget-settings';
import {DexTransferWidgetSettingsService} from './settings/dex-transfer-widget-settings.service';

export class DexTransferWidgetImplementation implements WidgetImplementation {

    public settings: DexTransferWidgetSettings;

    public settingsService: DexTransferWidgetSettingsService = this.injector
        .get(DexTransferWidgetSettingsService);

    public ready$ = new BehaviorSubject<boolean>(false);
    public event$ = new Subject<WidgetImplementationEvent>();

    public readonly settingsWithUpdates$: Observable<Partial<DexLpTradeWidgetSettings>> =
                        getWidgetSettingsWithUpdatesStream(this);

    public readonly initialDependency$ = this.injector.get(DexStreamingInitResolverService).resolve();

    private readonly dexSecuritiesSubjectService = this.injector.get(DexSecuritiesSubjectService);
    private readonly appConfigService = this.injector.get(AppConfigService);
    private readonly dexTrancheUtilityService = this.injector.get(DexTrancheUtilityService);
    private readonly dynamicZyxDaemonService = this.injector.get(DynamicZyxDaemonService);

    constructor(
        protected injector: Injector
    ) {}

    public setSettings(
        settings?: DexTransferWidgetSettings,
        flags?: WidgetImplementationEventFlags
    ): Observable<DexTransferWidgetSettings> {
        return setWidgetSettings(this, settings, flags);
    }

    public updateSettings(
        updates?: Partial<DexTransferWidgetSettings>,
        flags?: WidgetImplementationEventFlags
    ): Observable<Partial<DexTransferWidgetSettings>> {
        return updateWidgetSettings(this, updates, flags);
    }

    public resolveSettings(
        settings: Partial<DexTransferWidgetSettings>
    ): Observable<Partial<DexTransferWidgetSettings>> {
        return pipeSettingsCorrections<DexTransferWidgetSettings>([
            s => this.getAssetFix(s, this.settings),
            s => this.getAddressFieldFix(s, this.settings),
            s => this.getQtyFieldFix(s, this.settings)
        ])(settings, this);
    }

    private getAssetFix<S extends DexTransferWidgetSettings>(
        u: Partial<S>,
        prevS: Partial<S>
    ): Observable<{assetSymbol: string} | undefined> {
        const symbol = getSettingsWithUpdateField(prevS as DexTransferWidgetSettings, u, 'assetSymbol');
        const isAssetUpdated = this.dexTrancheUtilityService.isAssetSymbolUpdated(u, prevS);

        if (symbol && !isAssetUpdated) {
            return of(undefined);
        }

        const d = symbol && this.dexSecuritiesSubjectService.getDeliverableBySymbol(symbol);

        if (!d) {
            const nativeSymbol = this.appConfigService.appConfig.dex.chain.nativeCurrency.symbol;

            return of({assetSymbol: nativeSymbol});
        }

        return of(undefined);
    }

    private getAddressFieldFix<S extends DexTransferWidgetSettings>(
        u: Partial<S>,
        prevS: Partial<S>
    ): Observable<Partial<{fromAddress: string}> | undefined> {
        const address = getSettingsWithUpdateField(prevS as DexTransferWidgetSettings, u, 'fromAddress');
        const isAddressUpdated = !isSettingsUpdateEmptyOrSame(prevS as DexTransferWidgetSettings, u, 'fromAddress');

        if (address && !isAddressUpdated) {
            return of(undefined);
        }

        const userWallets = this.dynamicZyxDaemonService.dynRemote.userWallets$.value;
        const isOwned = !!address && !!userWallets.find(w => w.address === address);

        if (isOwned) {
            return of(undefined);
        }

        const defaultWallet = this.dynamicZyxDaemonService.dynRemote.primaryWallet$.value;
        if (defaultWallet) {
            return of({fromAddress: defaultWallet.address});
        }

        return of(undefined);
    }

    private getQtyFieldFix<S extends DexTransferWidgetSettings>(
        u: Partial<S>,
        prevS: Partial<S>
    ): Observable<Partial<{qty: string}> | undefined> {
        const qtyStr = getSettingsWithUpdateField(prevS as DexTransferWidgetSettings, u, 'qty');
        const isQtyUpdated = !isSettingsUpdateEmptyOrSame(prevS as DexTransferWidgetSettings, u, 'qty');
        let qtyDec: Decimal | undefined;

        try {
            qtyDec = new Decimal(qtyStr);
        } catch (_e) {
            // Skip
        }

        if (qtyDec?.isFinite() && !isQtyUpdated) {
            return of(undefined);
        }

        const symbol = getSettingsWithUpdateField(prevS as DexTransferWidgetSettings, u, 'assetSymbol');
        const d = this.dexSecuritiesSubjectService.getDeliverableBySymbol(symbol);
        const qty = qtyDec && numToValueWithDel(qtyDec, d).v;
        const minQty = 1n;

        if (!qty || qty < minQty) {
            return of({qty: vToDec(minQty, d.decimalPlaces).toString()});
        }

        return of(undefined);
    }

}
